import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { seedTickets } from '../data/tickets';
import type { Ticket, TicketCategory, TicketContext as TicketSubject, TicketPriority, TicketStatus } from '../types/tickets';
interface RaiseInput {
  subject: string;
  category: TicketCategory;
  body: string;
  context: TicketSubject;
  confidential?: boolean;
}
interface TicketContextValue {
  tickets: Ticket[];
  raiseTicket: (input: RaiseInput) => Ticket;
  /** A reply from the reporter. Moves the ticket back to support. */
  reply: (ticketId: string, body: string) => void;
  /** A reply from an agent. Optionally an internal note the reporter never sees. */
  agentReply: (ticketId: string, body: string, options?: {
    internal?: boolean;
  }) => void;
  setStatus: (ticketId: string, status: TicketStatus) => void;
  setPriority: (ticketId: string, priority: TicketPriority) => void;
  assign: (ticketId: string, agent: string) => void;
  escalate: (ticketId: string, reason: string) => void;
  resolve: (ticketId: string, resolution: string) => void;
}
const Ctx = createContext<TicketContextValue | null>(null);
let counter = 0;
function nextReference(): string {
  counter += 1;
  return `SUP-${105000 + counter}`;
}
export function TicketProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [tickets, setTickets] = useState<Ticket[]>(seedTickets);
  const touch = useCallback((ticketId: string, change: (ticket: Ticket) => Ticket) => {
    setTickets(current => current.map(ticket => ticket.id === ticketId ? {
      ...change(ticket),
      updatedAt: new Date().toISOString()
    } : ticket));
  }, []);
  const raiseTicket = useCallback<TicketContextValue['raiseTicket']>(input => {
    const now = new Date().toISOString();
    const ticket: Ticket = {
      id: `tkt-${Date.now()}`,
      reference: nextReference(),
      subject: input.subject,
      category: input.category,
      status: 'Open',
      priority: input.category === 'Suspicious activity' ? 'Urgent' : 'Normal',
      openedAt: now,
      updatedAt: now,
      raisedBy: 'saviour.a',
      assignedTo: null,
      context: input.context,
      confidential: input.confidential,
      resolution: null,
      messages: [{
        id: `msg-${Date.now()}`,
        author: 'you',
        authorName: 'You',
        body: input.body,
        at: now
      }, {
        id: `msg-${Date.now() + 1}`,
        author: 'system',
        authorName: 'Campuzon',
        body: 'Received. A support agent will pick this up. Safety and financial reports are looked at first.',
        at: now
      }]
    };
    setTickets(current => [ticket, ...current]);
    return ticket;
  }, []);
  const reply = useCallback<TicketContextValue['reply']>((ticketId, body) => {
    touch(ticketId, ticket => ({
      ...ticket,
      status: ticket.status === 'Resolved' || ticket.status === 'Closed' ? 'Open' : 'With support',
      messages: [...ticket.messages, {
        id: `msg-${Date.now()}`,
        author: 'you',
        authorName: 'You',
        body,
        at: new Date().toISOString()
      }]
    }));
  }, [touch]);
  const agentReply = useCallback<TicketContextValue['agentReply']>((ticketId, body, options) => {
    touch(ticketId, ticket => ({
      ...ticket,
      status: options?.internal ? ticket.status : 'Awaiting your reply',
      messages: [...ticket.messages, {
        id: `msg-${Date.now()}`,
        author: 'support',
        authorName: 'Support — you',
        body,
        at: new Date().toISOString(),
        internal: options?.internal
      }]
    }));
  }, [touch]);
  const setStatus = useCallback<TicketContextValue['setStatus']>((ticketId, status) => touch(ticketId, ticket => ({
    ...ticket,
    status
  })), [touch]);
  const setPriority = useCallback<TicketContextValue['setPriority']>((ticketId, priority) => touch(ticketId, ticket => ({
    ...ticket,
    priority
  })), [touch]);
  const assign = useCallback<TicketContextValue['assign']>((ticketId, agent) => touch(ticketId, ticket => ({
    ...ticket,
    assignedTo: agent
  })), [touch]);
  const escalate = useCallback<TicketContextValue['escalate']>((ticketId, reason) => {
    touch(ticketId, ticket => ({
      ...ticket,
      status: 'Escalated',
      priority: 'High',
      messages: [...ticket.messages, {
        id: `msg-${Date.now()}`,
        author: 'system',
        authorName: 'Campuzon',
        body: `Escalated: ${reason}`,
        at: new Date().toISOString()
      }]
    }));
  }, [touch]);
  const resolve = useCallback<TicketContextValue['resolve']>((ticketId, resolution) => {
    touch(ticketId, ticket => ({
      ...ticket,
      status: 'Resolved',
      resolution,
      messages: [...ticket.messages, {
        id: `msg-${Date.now()}`,
        author: 'support',
        authorName: 'Support — you',
        body: resolution,
        at: new Date().toISOString()
      }]
    }));
  }, [touch]);
  const value = useMemo(() => ({
    tickets,
    raiseTicket,
    reply,
    agentReply,
    setStatus,
    setPriority,
    assign,
    escalate,
    resolve
  }), [tickets, raiseTicket, reply, agentReply, setStatus, setPriority, assign, escalate, resolve]);
  return <Ctx.Provider value={value} data-id="element-5406" data-mp="1bw0bz7y8lldj">{children}</Ctx.Provider>;
}
export function useTickets(): TicketContextValue {
  const value = useContext(Ctx);
  if (!value) throw new Error('useTickets must be used inside TicketProvider');
  return value;
}